import Link from "next/link";
import type { PageSummary } from "@/types/page";
import type { MenuItemDto } from "@/types/menu";
import { cn } from "@/lib/utils";

interface NavProps {
  pages?: PageSummary[];
  menuItems?: MenuItemDto[];
  className?: string;
  ariaLabel?: string;
  onNavigate?: () => void;
}

function NavLink({
  href,
  label,
  target,
  onNavigate,
}: {
  href: string;
  label: string;
  target?: string;
  onNavigate?: () => void;
}) {
  const external = target === "_blank" || href.startsWith("http");
  const isMailto = href.startsWith("mailto:");
  const className =
    "inline-flex min-h-[44px] items-center rounded-[var(--border-radius)] px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-primary)]";
  if (external) {
    return (
      <a href={href} target={target} rel="noopener noreferrer" onClick={onNavigate} className={className}>
        {label}
        {isMailto ? <span className="sr-only"> — opens your email application</span> : null}
      </a>
    );
  }
  return (
    <Link href={href} onClick={onNavigate} className={className}>
      {label}
      {isMailto ? <span className="sr-only"> — opens your email application</span> : null}
    </Link>
  );
}

function MenuTree({ items, onNavigate }: { items: MenuItemDto[]; onNavigate?: () => void }) {
  return (
    <>
      {items.map((item) => (
        <div key={item.id} className="group relative">
          <NavLink
            href={item.href}
            label={item.label}
            target={item.target}
            onNavigate={onNavigate}
          />
          {item.children && item.children.length > 0 && (
            <div data-nav-children className="hidden pl-4 group-hover:block group-focus-within:block md:absolute md:left-0 md:top-full md:z-50 md:min-w-[12rem] md:rounded-md md:border md:border-[var(--color-border)] md:bg-white md:p-2 md:pl-0 md:shadow-lg">
              <MenuTree items={item.children} onNavigate={onNavigate} />
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export function Nav({ pages = [], menuItems = [], className, ariaLabel, onNavigate }: NavProps) {
  const useMenu = menuItems.length > 0;

  return (
    <nav
      data-nav={ariaLabel === "Mobile" ? "mobile" : "desktop"}
      role="navigation"
      aria-label={ariaLabel}
      className={cn("flex flex-col gap-1 md:flex-row md:items-center md:gap-[calc(var(--space-unit)*1)]", className)}
    >
      {useMenu ? (
        <MenuTree items={menuItems} onNavigate={onNavigate} />
      ) : (
        pages.filter((page) => !page.passwordProtected).map((page) => (
          <NavLink
            key={page.id}
            href={page.frontPage || page.slug === "home" ? "/" : `/${page.slug}`}
            label={page.title}
            onNavigate={onNavigate}
          />
        ))
      )}
    </nav>
  );
}
